/**
 * Pure, render-free derivations for the Person Detail surface.
 *
 * Everything here is a total function over the canonical security
 * read model so the routing, empty-state and aggregation behaviour
 * can be unit tested without a DOM, a router or a network.
 */
import type {
  AuditEvent,
  PersonActivity,
  PersonDetail,
  PersonPasskey,
  PersonSession,
} from "../../lib/types";

export const PERSON_TABS=[
  "general",
  "contact",
  "addresses",
  "security",
  "access",
  "activity",
]as const;

export type PersonTab=(typeof PERSON_TABS)[number];

export function isPersonTab(value:string|null|undefined):value is PersonTab{
  return PERSON_TABS.some(tab=>tab===value);
}

const UUID_RE=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The API path parameter is a UUID. A malformed id is a client-side
 * navigation mistake, not an authorization or transport failure, so it
 * must never be sent to the API and must never surface a raw server error.
 */
export function isValidSubjectId(value:string|null|undefined):boolean{
  return typeof value==="string"&&UUID_RE.test(value.trim());
}

/**
 * Distinguishes the three failure classes Person Detail can be in.
 * `unresolved` must stay client-side (no request was issued).
 */
export type PersonDetailFailure="unresolved"|"not-found"|"guarded";

export function classifyPersonDetailError(
  error:string|null,
):PersonDetailFailure|null{
  if(!error)return null;
  if(/person_not_found|not_found|404/i.test(error))return "not-found";
  if(/passkey_session_required|authentication_required/i.test(error))return "guarded";
  if(/request_failed_4\d\d|permission_denied/i.test(error))return "guarded";
  return null;
}

export interface PersonSecurityCounts{
  activePasskeys:number;
  unlockPasskeys:number;
  trustGroups:number;
  activeSessions:number;
}

export function countSecurityPosture(
  passkeys:PersonPasskey[],
  sessions:PersonSession[],
):PersonSecurityCounts{
  const active=passkeys.filter(p=>p.status==="ACTIVE");
  return {
    activePasskeys:active.length,
    unlockPasskeys:active.filter(p=>p.unlock_capable).length,
    trustGroups:new Set(active.map(p=>p.trust_group)).size,
    activeSessions:sessions.filter(s=>s.active).length,
  };
}

export function personDisplayName(detail:PersonDetail|null):string{
  const profile=detail?.profile;
  const person=detail?.person;
  return profile?.preferred_name
    ||profile?.display_name
    ||person?.display_name
    ||person?.subject_name
    ||"";
}

export function personEmail(detail:PersonDetail|null):string{
  return detail?.profile?.primary_email||detail?.profile?.auth_email||"";
}

/**
 * Where the address on file came from, as a canonical machine value.
 * The caller localizes the label and must not mutate the value.
 */
export type EmailSource="person-profile"|"auth-user"|"none";

export function personEmailSource(detail:PersonDetail|null):EmailSource{
  if(detail?.profile?.primary_email)return "person-profile";
  if(detail?.profile?.auth_email)return "auth-user";
  return "none";
}

export function personEvents(activity:PersonActivity|null):AuditEvent[]{
  return activity?.events??[];
}

export function personRoles(detail:PersonDetail|null):string[]{
  return detail?.person?.role_keys??[];
}

export function personPackages(detail:PersonDetail|null):string[]{
  return detail?.person?.package_keys??[];
}

export function sessionKey(session:PersonSession):string{
  return session.session_id||session.id;
}

export type RecoveryTone="positive"|"attention"|"critical";

export function recoveryTone(recoveryState:string|null|undefined):RecoveryTone{
  if(recoveryState==="HEALTHY")return "positive";
  if(recoveryState==="CRITICAL")return "critical";
  return "attention";
}

export type SeverityTone="positive"|"neutral"|"attention"|"critical";

export function severityTone(severity:string):SeverityTone{
  if(severity==="CRITICAL")return "critical";
  if(severity==="WARNING")return "attention";
  if(severity==="NOTICE")return "neutral";
  return "positive";
}